import type { Deployment } from '../api/pods';

export type Tone = 'green' | 'red' | 'amber' | 'grey';

const RED = new Set([
  'CrashLoopBackOff',
  'Error',
  'OOMKilled',
  'ImagePullBackOff',
  'ErrImagePull',
  'InvalidImageName',
  'CreateContainerConfigError',
  'CreateContainerError',
  'RunContainerError',
  'ContainerCannotRun',
  'Failed',
  'Evicted',
  'DeadlineExceeded',
]);
const AMBER = new Set(['Pending', 'ContainerCreating', 'PodInitializing', 'Terminating', 'Unknown']);
const GREY = new Set(['Completed', 'Succeeded']);

/** kubectl-style pod status → chip tone: Running green, crash/pull/OOM errors red, starting/pending amber, done grey. */
export function podStatusTone(status: string): Tone {
  if (status === 'Running') return 'green';
  if (
    RED.has(status) ||
    /^Init:(CrashLoopBackOff|Error|OOMKilled|ImagePullBackOff|ErrImagePull)$/.test(status)
  )
    return 'red';
  if (GREY.has(status)) return 'grey';
  if (AMBER.has(status) || status.startsWith('Init:')) return 'amber';
  return 'amber';
}

/** `eu.gcr.io/proj/api@sha256:abcdef…` → `api@abcdef12`; `eu.gcr.io/proj/api:1.2.3` → `api:1.2.3`. */
export function shortImage(image: string): string {
  const last = image.slice(image.lastIndexOf('/') + 1);
  const at = last.indexOf('@');
  if (at >= 0) {
    const digest = last.slice(at + 1).replace(/^sha256:/, '');
    return `${last.slice(0, at)}@${digest.slice(0, 8)}`;
  }
  return last;
}

/** kubectl-style age: `45s`, `12m`, `3h20m`, `5d4h`. */
export function formatAge(from: string | null | undefined, now: number = Date.now()): string {
  if (!from) return '—';
  const t = Date.parse(from);
  if (!Number.isFinite(t)) return '—';
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 && d < 10 ? `${d}d${h % 24}h` : `${d}d`;
}

/** Conditions that need attention: Available/Progressing not True, or any ReplicaFailure True. */
export function deploymentProblems(d: Deployment): string[] {
  return d.conditions
    .filter((c) =>
      c.type === 'ReplicaFailure'
        ? c.status === 'True'
        : c.type === 'Available' || c.type === 'Progressing'
          ? c.status !== 'True'
          : false
    )
    .map((c) => `${c.type}: ${c.reason || c.status}${c.message ? ` — ${c.message}` : ''}`);
}

/** Group by namespace, keys sorted, items by name. */
export function byNamespace<T extends { namespace: string; name: string }>(items: T[]): [string, T[]][] {
  const m = new Map<string, T[]>();
  for (const i of items) m.set(i.namespace, [...(m.get(i.namespace) ?? []), i]);
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ns, list]) => [ns, [...list].sort((a, b) => a.name.localeCompare(b.name))]);
}

/** k8s-style CPU: `250m` below one core, `1.5` cores above. */
export function formatCores(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (v === 0) return '0';
  if (v < 1) return `${Math.max(1, Math.round(v * 1000))}m`;
  return `${Number(v.toFixed(2))} cores`;
}
